import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { INVITE_TO_EDIT_KEY } from '@confluence/web-item/entry-points/webItemKeys.ts';
import { MANAGE_WATCHER_TAB_TYPES } from '@confluence/manage-watcher-dialog/entry-points/manage-watcher-tab-types.ts';
import { PAGE_BUTTONS } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { WebItemLocation } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import { useAutomationUrlParamTrigger } from '@confluence/automation-menu/entry-points/useAutomationUrlParamTrigger.tsx';
import { WebItemButtonList } from '@confluence/web-item-list/entry-points/WebItemButtonList.ts';
import { FavouriteButton } from '@confluence/favourite-button/entry-points/FavouriteButton.ts';
import { PageHeaderLoomEntryPoint } from '@confluence/page-header-loom-button/entry-points/PageHeaderLoomEntryPoint.ts';
import { WatchDialogWrapper } from '@confluence/watch-dialog/entry-points/WatchDialogWrapper.tsx';
import { WATCH_DIALOG_LOCATION } from '@confluence/watch-dialog/entry-points/WatchDialogLocation.tsx';
import { CommentButtonWrapper } from '@confluence/comment-button/entry-points/CommentButtonWrapper.ts';
import { ShareAndRestrictButton } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictButton.tsx';
import { ShareAndRestrictButtonPlaceholder } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictButtonPlaceholder.ts';
import { useIsLoomDisabled } from '@confluence/loom-utils/entry-points/useIsLoomDisabled.ts';

import { GroupedPageButtonsCommon, FILTERED_WEB_ITEM_KEYS } from './GroupedPageButtonsCommon.js';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledWebItemButtonList = styled(WebItemButtonList)({
	verticalAlign: 'middle',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.div({
	alignItems: 'center',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledBorder = styled.div({
	borderLeft: `${token('border.width.selected')} solid ${token('color.border')}`,
	display: 'inline-block',
	width: '1px',
	height: '16px',
	margin: `0 ${token('space.075')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WebItemsContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
});

// DISCO-557 add maxWidth to system content button panel to limit amount of pageheader horizontal space that can be taken up by third party buttons
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SystemContentButtonMaxWidthContainer = styled.div({
	maxWidth: '200px',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShareButtonPlaceholderSpacer = styled.span({
	marginLeft: token('space.100'),
});

const ContentHeaderShareAndCopyButtons = ({
	contentId,
	versionOverride,
	contentType,
	contentSubType,
	isArchived,
}) => {
	return (
		<ShareAndCopyButtons
			contentId={contentId}
			versionOverride={versionOverride}
			contentType={contentType}
			contentSubType={contentSubType}
			isArchived={isArchived}
		/>
	);
};

ContentHeaderShareAndCopyButtons.propTypes = {
	contentId: PropTypes.string.isRequired,
	versionOverride: PropTypes.number,
	contentType: PropTypes.string,
	contentSubType: PropTypes.string,
	isArchived: PropTypes.bool,
};

const ShareAndCopyButtons = ({
	contentId,
	versionOverride,
	contentType,
	contentSubType,
	isArchived,
}) => {
	return (
		<StyledContainer data-vc="share-button-copy-link-container">
			{/* Share button */}
			<WebItemLocation
				key={`share-button${isArchived ? '-archived' : ''}`}
				tagName="span"
				contentId={contentId}
				allowedWebItems={[INVITE_TO_EDIT_KEY]}
				location={PAGE_BUTTONS}
				version={versionOverride}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ display: 'flex' }}
				renderWhenLoading
			>
				{({ loading, webItems }) => {
					if (loading) {
						return (
							<>
								<UFOLoadHold name="ShareButtonPlaceholderSpacer" hold={loading} />
								<ShareButtonPlaceholderSpacer>
									<ShareAndRestrictButtonPlaceholder />
								</ShareButtonPlaceholderSpacer>
							</>
						);
					}
					webItems.forEach((item) => {
						if (item.completeKey === INVITE_TO_EDIT_KEY) {
							// ShareButton can now fetch with a provided contentId
							item.contentId = contentId;
							item.contentType = contentType;
						}
					});

					return (
						<StyledWebItemButtonList
							pageId={contentId}
							webItems={webItems}
							id="extracted-share-button"
							contentSubType={contentSubType}
							source="contentHeader"
						/>
					);
				}}
			</WebItemLocation>
		</StyledContainer>
	);
};

ShareAndCopyButtons.propTypes = {
	contentId: PropTypes.string.isRequired,
	addItem: PropTypes.func,
	versionOverride: PropTypes.number,
	contentType: PropTypes.string,
	contentSubType: PropTypes.string,
	isArchived: PropTypes.bool,
};

export const GroupedPageButtons = (props) => {
	const {
		contentId,
		versionOverride,
		isExternalCollaborator,
		spaceKey,
		contentSubType,
		contentType,
		isArchived,
	} = props;
	const isLoomDisabled = useIsLoomDisabled();

	// Automation manual trigger modal listening for query param
	useAutomationUrlParamTrigger();

	return (
		<GroupedPageButtonsCommon {...props}>
			<CommentButtonWrapper
				key={contentId}
				contentId={contentId}
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				isCommentButtonSSRd={Boolean(window.__SSR_RENDERED__ || process.env.REACT_SSR)}
			/>
			{!isArchived && !isLoomDisabled && (
				<PageHeaderLoomEntryPoint
					contentId={contentId}
					spaceKey={spaceKey}
					contentType={contentType}
				/>
			)}
			<FavouriteButton key="favourite-button" contentId={contentId} useLegacyIcon />
			<WatchDialogWrapper
				key="watch-dialog"
				contentId={contentId}
				defaultTab={MANAGE_WATCHER_TAB_TYPES.PAGE}
				componentLocation={WATCH_DIALOG_LOCATION.PAGE}
			/>
			{/* Buttons other than edit, favourite, watch and share */}
			<WebItemLocation
				key="page-buttons"
				contentId={contentId}
				location={PAGE_BUTTONS}
				notAllowedWebItems={FILTERED_WEB_ITEM_KEYS}
				tagName="span"
				version={versionOverride}
			>
				{({ webItems }) => (
					<WebItemsContainer>
						{!!webItems.length && <StyledBorder />}
						<SystemContentButtonMaxWidthContainer>
							<StyledWebItemButtonList
								webItems={webItems}
								buttonType="aui-button-subtle"
								contentSubType={contentSubType}
							/>
						</SystemContentButtonMaxWidthContainer>
					</WebItemsContainer>
				)}
			</WebItemLocation>

			{!isExternalCollaborator && (
				<>
					<StyledBorder />
					{isArchived && (
						<ShareAndRestrictButton
							contentId={contentId}
							contentType={contentType}
							source="contentHeader"
							isNotifyDisabled
						/>
					)}
					<ContentHeaderShareAndCopyButtons
						contentId={contentId}
						versionOverride={versionOverride}
						contentType={contentType}
						contentSubType={contentSubType}
						isArchived={isArchived}
					/>
				</>
			)}
		</GroupedPageButtonsCommon>
	);
};

GroupedPageButtons.propTypes = {
	spaceKey: PropTypes.string.isRequired,
	contentId: PropTypes.string.isRequired,
	versionOverride: PropTypes.number,
	headerSticky: PropTypes.bool,
	contentType: PropTypes.string,
	contentSubType: PropTypes.string,
	isExternalCollaborator: PropTypes.bool,
	lastModifiedDate: PropTypes.string,
	isArchived: PropTypes.bool,
};
